import {
    Dialog,
    DialogPanel,
    DialogBackdrop,
    CloseButton,
} from "@headlessui/react";
import Icon from "@web-ui/components/Icon";

/*
 * Modal: the kit's dialog on the motion layer (owner 2026-10-13): the panel is a motion-sheet (it scales up
 * from 0.96 as its backdrop fades at --motion-slow on --ease-out-quint; a slide panel comes in from the
 * end edge), Headless UI's data-closed drives both. Three widths: sm (480px, a confirm), md (572px, the
 * kit's own, the default), lg (768px, a dialog that holds a list). classWrapper still appends.
 */
type ModalProps = {
    className?: string;
    classWrapper?: string;
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    isSlidePanel?: boolean;
    /** The close button's accessible name, for apps that translate (English by default). */
    closeLabel?: string;
    size?: "sm" | "md" | "lg";
};

const WIDTH = { sm: "max-w-120", md: "max-w-143", lg: "max-w-3xl" } as const;

const CloseButtonModal = ({ label }: { label: string }) => {
    return (
        <CloseButton aria-label={label} className="group fixed end-5 top-5 z-15 size-12 border border-transparent bg-b-surface2-overlay rounded-full text-0 max-md:top-3 max-md:end-3 dark:border-s-stroke2">
            <Icon
                className="fill-t-secondary transition-colors group-hover:fill-t-primary"
                name="close"
            />
        </CloseButton>
    );
};

const Modal = ({
    className,
    classWrapper,
    open,
    onClose,
    children,
    isSlidePanel,
    closeLabel = "Close",
    size = "md",
}: ModalProps) => {
    return (
        <Dialog className="relative z-50" open={open} onClose={onClose}>
            <DialogBackdrop
                className={`fixed inset-0 motion-backdrop ${
                    isSlidePanel
                        ? "bg-shade-07/70 dark:bg-shade-04/90"
                        : "bg-shade-04/90"
                }`}
                transition
            />
            <div
                className={`fixed inset-0 flex w-screen ${
                    isSlidePanel
                        ? "justify-end overflow-hidden"
                        : "justify-center p-4 overflow-y-auto"
                } ${className || ""}`}
            >
                <DialogPanel
                    className={`bg-b-surface1 ${
                        isSlidePanel
                            ? "relative w-114 h-svh motion-sheet motion-sheet--slide max-md:w-full"
                            : `w-full ${WIDTH[size]} m-auto p-12 shadow-depth rounded-[2.5rem] motion-sheet max-md:p-6 dark:shadow-[var(--shadow-overlay)]`
                    } ${classWrapper || ""}`}
                    transition
                >
                    {children}
                    {isSlidePanel && <CloseButtonModal label={closeLabel} />}
                </DialogPanel>
                {!isSlidePanel && <CloseButtonModal label={closeLabel} />}
            </div>
        </Dialog>
    );
};

export default Modal;
